/**
 * Background-tab poll throttling.
 *
 * Every relay polling loop (accountStore, headroom, me/snapshot, poly hooks,
 * rate-limit status) runs at full rate in hidden tabs, and popped-out windows
 * (FillsPopup) are whole extra JS contexts running the same
 * loops — a single trader with a popup idles at ~200+ relay requests/min.
 * Hidden tabs don't need trading-grade freshness: throttle them to 1/6 rate
 * and fire one immediate tick on refocus so the visible data heals instantly.
 *
 * `startVisibilityAwareInterval` replaces the bare `setInterval(tick, ms)` +
 * `clearInterval` pair. The interval keeps ticking at the base cadence but the
 * tick is gated while `document.hidden` — only every HIDDEN_DIVISOR-th firing
 * runs — so there's no timer re-arming and the resume math stays trivial.
 * Precedent for visibility-aware polling: useHydra / useGameStatesPoll.
 */

export const HIDDEN_DIVISOR = 6;

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * Run `tick` every `visibleMs` while the tab is visible, every
 * `visibleMs × HIDDEN_DIVISOR` while hidden, with one immediate tick on
 * becoming visible again. Returns a stop function (replaces clearInterval).
 * Does NOT fire an initial tick — call sites that poll-then-schedule keep
 * doing that themselves.
 */
export function startVisibilityAwareInterval(
  tick: () => void,
  visibleMs: number,
  hiddenDivisor = HIDDEN_DIVISOR,
): () => void {
  let skips = 0;
  const gated = () => {
    if (isHidden()) {
      skips += 1;
      if (skips % hiddenDivisor !== 0) return;
    } else {
      skips = 0;
    }
    tick();
  };
  const timer = setInterval(gated, visibleMs);
  const onVisibilityChange = () => {
    if (!isHidden()) {
      skips = 0;
      tick();
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  return () => {
    clearInterval(timer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
  };
}

/**
 * For loops that pick their own delay per tick (recursive setTimeout, e.g.
 * useRateLimitStatus): multiply the chosen delay by this. 1 when visible,
 * HIDDEN_DIVISOR when hidden.
 */
export function hiddenPollMultiplier(): number {
  return isHidden() ? HIDDEN_DIVISOR : 1;
}

/**
 * Subscribe to "tab became visible" for loops that manage their own timers.
 * Returns an unsubscribe function.
 */
export function onTabVisible(cb: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  const handler = () => {
    if (!isHidden()) cb();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}
